import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, Check, MapPin, Phone } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/constants/site";
import { getTown } from "@/constants/areas";

export default function TownPage() {
  const { slug } = useParams();
  const town = getTown(slug);

  useEffect(() => {
    if (town) {
      document.title = `Junk Removal in ${town.name} | Take The Leap Junk Removal`;
      window.scrollTo({ top: 0 });
    }
  }, [town]);

  if (!town) return <Navigate to="/service-areas" replace />;

  return (
    <div data-testid="town-page" className="bg-ink font-sans text-zinc-100 antialiased">
      <div className="noise-overlay" aria-hidden="true" />
      <Nav />
      <main className="pt-24">
        <section className="mx-auto max-w-7xl px-6 pb-10 pt-16 md:px-10 md:pt-24">
          <Reveal>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.35em] text-acid">
              Service area · {town.tag}
            </p>
            <h1 className="max-w-4xl font-display text-5xl font-black italic uppercase leading-[0.9] tracking-tighter sm:text-6xl lg:text-7xl">
              Junk removal in <span className="text-acid">{town.name}</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-400">{town.blurb}</p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                data-testid="town-quote-btn"
                to="/#quote"
                className="group flex items-center gap-2 bg-acid px-8 py-4 font-display text-xl font-extrabold italic uppercase tracking-tight text-black transition-colors duration-200 hover:bg-white"
              >
                Get a free quote
                <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
              <a
                data-testid="town-call-btn"
                href={`tel:${SITE.phoneTel}`}
                className="flex items-center gap-3 border border-zinc-700 px-8 py-4 font-display text-xl font-extrabold italic uppercase tracking-tight text-white transition-colors duration-200 hover:border-acid hover:text-acid"
              >
                <Phone className="h-5 w-5" />
                {SITE.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </section>

        <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10">
          <div className="grid gap-6 md:grid-cols-12 md:gap-8">
            <Reveal className="md:col-span-7">
              <div className="h-full border border-seam bg-coal p-8 md:p-10">
                <h2 className="font-display text-2xl font-extrabold italic uppercase tracking-tight">
                  Popular jobs in {town.name.split(",")[0]}
                </h2>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                  {town.jobs.map((job) => (
                    <li key={job} className="flex items-center gap-3 text-sm text-zinc-300">
                      <Check className="h-4 w-4 shrink-0 text-acid" />
                      {job}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-xs leading-relaxed text-zinc-500">
                  Note: we can’t take hazardous materials — including anything with Freon
                  (fridges, freezers, AC units). Not sure? Just ask when you call.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="md:col-span-5">
              <div className="h-full border border-seam bg-ink p-8 md:p-10">
                <h2 className="font-display text-2xl font-extrabold italic uppercase tracking-tight">
                  Also serving nearby
                </h2>
                <p className="mt-6 flex items-start gap-2 text-xs font-semibold uppercase tracking-[0.15em] leading-loose text-zinc-400">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-acid" />
                  {town.towns}
                </p>
                <Link
                  data-testid="town-all-areas-link"
                  to="/service-areas"
                  className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-acid hover:underline"
                >
                  View all service areas <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="border-t border-seam bg-coal">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-6 py-16 md:px-10">
            <Reveal>
              <h2 className="font-display text-3xl font-black italic uppercase tracking-tight sm:text-4xl">
                Junk in {town.name.split(",")[0]}? <span className="text-acid">Take the leap.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="max-w-md text-sm leading-relaxed text-zinc-400">
                Call <a href={`tel:${SITE.phoneTel}`} className="font-bold text-acid hover:underline">{SITE.phoneDisplay}</a> or
                send the free quote form — we’ll call you back with a firm price, usually within the hour.
              </p>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
